import { useNavigate, useSearch } from "@tanstack/react-router";
import type { VideoMetrics, VideoMetricsSort } from "@unbox/api";
import { FilterLines, Route, Scan, SearchMd, VideoRecorder } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePagination } from "@/api/pagination";
import { useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import {
  ExportLimitExceededError,
  useVideoMetricRowsFetcher,
  useVideoMetrics,
  useVideoMetricsDownload,
  useVideosCount,
  type VideoMetricsResponse,
} from "@/api/video-metrics";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { ActionRecordDialog } from "@/components/assurance/action-record-dialog";
import { AddRiskModal } from "@/components/assurance/add-risk-bulk-modal";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { ManageCreatorDropdown, VideoActionsDropdown } from "@/components/assurance/video-action-menus";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { BlockCreatorModal } from "@/components/blocklist/block-creator-modal";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { FilterPills } from "@/components/filters/filter-pills";
import { VideoFilterPanel } from "@/components/filters/panels";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { PageLayout } from "@/components/page-layout";
import { Dialog } from "@/components/ui/dialog";
import { VideoDetails } from "@/components/video-details";
import { generateFilterSyncConfig, useAutoFilterUrlSync } from "@/hooks/use-auto-filter-sync";
import { useCache } from "@/hooks/use-cache";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { useSearchUrlSync } from "@/hooks/use-search-url-sync";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useSort } from "@/hooks/use-sort";
import { useUrlFilterSync } from "@/hooks/use-url-filter-sync";
import type { ActionType } from "@/lib/assurance/action-types";
import { ADS_NOT_CONNECTED_TOOLTIP, getLogActionLabel } from "@/lib/assurance/action-types";
import { BulkCohortModal } from "@/pages/creator-cohorts/bulk-cohort-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { Metrics } from "@/pages/explorers/utils";
import { VideoExplorerTable } from "@/pages/explorers/video-explorer-table";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import { error as toastError } from "@/providers/ToastProvider";
import type { videoExplorerRoute } from "@/router";
import { cx } from "@/utils/cx";
import { downloadCSV } from "@/utils/download-csv";
import { compactNumberFormatter } from "@/utils/formatNumber";

const transformVideoData = (videos: VideoMetricsResponse["videos"]) => {
  return videos.map((video) => ({
    ...video,
    unitSalesChange: video.trendUnitSales ? (video.unitSales - video.trendUnitSales) / video.trendUnitSales : null,
    aovChange: video.trendAov && video.aov ? (video.aov - video.trendAov) / video.trendAov : null,
    ctrChange: video.trendCtr && video.ctr ? (video.ctr - video.trendCtr) / video.trendCtr : null,
    ctorChange: video.trendCtor && video.ctor ? (video.ctor - video.trendCtor) / video.trendCtor : null,
    gpmChange: video.trendGpm && video.gpm ? (video.gpm - video.trendGpm) / video.trendGpm : null,
  }));
};

const isMoreThanDayAgo = (date: Date): boolean => {
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return date < oneDayAgo;
};

type VideoData = {
  videoId: string;
  creatorId: string | null;
  creatorName: string | null;
  tiktokCreatorOpenId: string | null;
  thumbnail: string | null;
  productList?: string[];
};

export type VideoRow = ReturnType<typeof transformVideoData>[number];

const VideoExplorerContent = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof videoExplorerRoute)["useSearch"]>;
  const { video_id, search: urlSearch } = urlParams;
  const navigate = useNavigate() as ReturnType<(typeof videoExplorerRoute)["useNavigate"]>;
  const [search, setSearch] = useState(urlSearch || "");
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);
  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<VideoMetricsSort>("gmv");
  const sortByTitle = useCallback(
    () => setFromDescriptor({ column: "videoTitle", direction: "ascending" }),
    [setFromDescriptor],
  );
  const isAssurance = useIsAssurance();

  const { form, apiFilters, reset, activeFields, hasActiveFilters: hasActiveFilterFields } = useFilters();
  const timePeriod = (form.watch("timePeriod") as "7d" | "30d" | "all_time") || "7d";

  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
  const [bulkPreselectedAction, setBulkPreselectedAction] = useState<ActionType | undefined>();
  const [bulkPreselectedCategory, setBulkPreselectedCategory] = useState<"take" | "log" | undefined>();
  const [bulkMessageOpen, setBulkMessageOpen] = useState(false);
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [bulkBlockOpen, setBulkBlockOpen] = useState(false);
  const [bulkAddToListOpen, setBulkAddToListOpen] = useState(false);
  const [bulkAddRiskOpen, setBulkAddRiskOpen] = useState(false);
  const [bulkAddToCohortOpen, setBulkAddToCohortOpen] = useState(false);

  const { data: adsConnectionStatus } = useTikTokAdsConnectionStatus();
  const adsConnected = adsConnectionStatus?.connected === true;
  const adCampaignDisabledReason = adsConnected ? undefined : ADS_NOT_CONNECTED_TOOLTIP;

  // Automatically sync URL params to form based on active fields
  useAutoFilterUrlSync(form, urlParams, activeFields);

  // Generate field mapping and currentSearch dynamically
  const { fieldMapping, currentSearch } = useMemo(
    () => generateFilterSyncConfig(activeFields, urlParams),
    [activeFields, urlParams],
  );

  // Sync form changes back to URL
  useUrlFilterSync({
    form,
    fieldMapping,
    navigate,
    currentSearch,
  });

  // Sync search to URL
  useSearchUrlSync({
    search,
    setSearch,
    navigate,
    currentSearch: { search: urlSearch },
  });

  const filters = {
    ...apiFilters,
    text: debouncedSearch,
  };
  const { data: overview } = useDashboardData();
  const { data } = useVideoMetrics({
    timePeriod,
    pagination: pagination.pagination,
    sort,
    ...filters,
  });
  const { data: count } = useVideosCount({
    timePeriod,
    ...filters,
  });
  const total = useCache(count?.totalVideos);

  const tableData = useMemo(() => {
    if (!data) return undefined;
    return transformVideoData(data.videos);
  }, [data]);

  const {
    selectedKeys: selected,
    selectedData: selectedVideosData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<ReturnType<typeof transformVideoData>[number], VideoData>({
    getKey: useCallback((v: ReturnType<typeof transformVideoData>[number]) => v.videoId, []),
    toData: useCallback(
      (v: ReturnType<typeof transformVideoData>[number]) => ({
        videoId: v.videoId,
        creatorId: v.creatorId,
        creatorName: v.creatorName ?? null,
        tiktokCreatorOpenId: v.tiktokCreatorOpenId ?? null,
        thumbnail: v.thumbnail ?? null,
        productList: v.productList,
      }),
      [],
    ),
  });

  const handleSelectionChange = useMemo(() => createSelectionHandler(tableData), [createSelectionHandler, tableData]);

  const fetchMetricRows = useVideoMetricRowsFetcher({ timePeriod, sort, ...filters });
  const fetchChunk = useCallback(
    async (offset: number, limit: number) => {
      const chunk = await fetchMetricRows(offset, limit);
      return { items: transformVideoData(chunk.items), total: chunk.total };
    },
    [fetchMetricRows],
  );
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total: total ?? 0,
    loadedCount: tableData?.length ?? 0,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify({ timePeriod, filters }),
  });
  const handleClearSelection = selectAll.clear;

  const bulkSelection = useMemo(() => ({ mode: "include" as const, videoIds: selected }), [selected]);

  const bulkCreators = useMemo(() => {
    const byCreator = new Map<string, VideoData>();
    for (const video of selectedVideosData.values()) {
      if (video.creatorId && !byCreator.has(video.creatorId)) {
        byCreator.set(video.creatorId, video);
      }
    }
    return Array.from(byCreator.values());
  }, [selectedVideosData]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const { mutateAsync, isPending: isDownloading } = useVideoMetricsDownload();
  const downloadTableDate = async () => {
    if (!total) return;
    try {
      const rows = await mutateAsync({
        total,
        timePeriod,
        sort,
        ...filters,
      });

      const assuranceCols: (keyof VideoMetrics)[] = isAssurance
        ? ["flagTypes", "riskTiers", "actionsTaken", "creatorContactedAt"]
        : [];
      const assuranceHeaders = isAssurance
        ? ["Risk Types", "Risk Tiers", "Actions Taken", "Creator Contacted >24h Ago"]
        : [];

      // Calculate max number of products across all rows
      const maxProducts = Math.max(...rows.map((r) => r.productIds?.length ?? 0), 0);

      // Create dynamic product columns
      const productCols = Array.from({ length: maxProducts }, (_, i) => `product${i + 1}` as const);
      const productHeaders = Array.from({ length: maxProducts }, (_, i) => `Product ${i + 1} ID`);

      // Transform rows to include individual product columns
      const transformedRows = rows.map((r) => {
        const productFields: Record<string, string> = {};
        for (let i = 0; i < maxProducts; i++) {
          productFields[`product${i + 1}`] = r.productIds?.[i]?.replace("PID-", "") ?? "";
        }
        return {
          ...r,
          ...productFields,
          videoId: r.videoId.replace("VID-", ""),
          creatorId: r.creatorId.replace("CID-", ""),
          actionsTaken: r.actionsTaken?.map((a) => getLogActionLabel(a as ActionType)),
          riskTiers: r.riskTiers.map((t) => `T${t}`),
          creatorContactedAt: r.creatorContactedAt
            ? isMoreThanDayAgo(new Date(r.creatorContactedAt))
              ? "Yes"
              : "No"
            : "N/A",
          isInAdCampaign: r.isInAdCampaign ? "Yes" : "No",
        };
      });

      downloadCSV(
        transformedRows,
        "video_explorer",
        [
          "videoId",
          "videoTitle",
          "videoUrl",
          "creatorId",
          "publishDate",
          "views",
          "gmv",
          "ctr",
          "ctor",
          "gpm",
          "aov",
          ...assuranceCols,
          "isInAdCampaign",
          ...productCols,
        ] as (keyof (typeof transformedRows)[number])[],
        [
          "Video ID",
          "Video Title",
          "Video URL",
          "Creator",
          "Publish Date",
          "Views",
          "GMV",
          "CTR",
          "CTOR",
          "GPM",
          "AOV",
          ...assuranceHeaders,
          "In Ad Campaign",
          ...productHeaders,
        ],
      );
    } catch (err) {
      if (err instanceof ExportLimitExceededError) {
        toastError(`Too many videos to export (${err.total.toLocaleString()})`, {
          description: `Please use filters to reduce the selection to ${err.limit.toLocaleString()} videos or less.`,
        });
      } else {
        throw err;
      }
    }
  };

  useEffect(() => {
    const subscription = form.watch(() => pagination.setPage(1));
    return subscription.unsubscribe;
  }, []);
  const [selectedVideoId, setSelectedVideoId] = useState<string | undefined>(video_id);

  const resetFilters = () => {
    setSearch("");
    clearSearch();
    reset();
  };

  return (
    <PageLayout
      drawerOpen={filtersOpen}
      title="All Videos"
      icon={VideoRecorder}
      subtitle="See how videos associated to your products are performing"
      callToAction={<ExportCsvButton onClick={downloadTableDate} isDownloading={isDownloading} label="Export as CSV" />}
    >
      <PageHero
        pageKey={PageHeroKey.AllVideos}
        title="Know what every video sells, and if it's safe."
        text1={{ icon: Scan, message: "AI scans every video for brand safety risks." }}
        text2={{ icon: Route, message: "Track performance over the last 30 days." }}
        text3={{ icon: VideoRecorder, message: "See which products each video sold, down to the unit." }}
        image="/heroes/all-videos.png"
      />
      <div className="flex flex-col gap-5 flex-1 min-h-0 min-w-0 w-full">
        <div className="flex flex-row gap-4 w-full flex-shrink-0 flex-wrap">
          <Metrics data={overview?.mainKPIs.weeklyVideos} />
          <Metrics data={overview?.videoKPIs.weeklyRevenueDrivingVideos} />
          <Metrics data={overview?.videoKPIs.weeklyAverageVideoGMV} />
        </div>
        <div className="flex flex-row items-center justify-between flex-shrink-0 relative flex-wrap gap-4">
          <span className="text-sm font-medium">
            {count ? `${compactNumberFormatter.format(count.totalVideos)} Videos` : "Loading..."}
          </span>
          <div className="flex items-center flex-row gap-2">
            <Input
              size="sm"
              inputClassName="text-sm"
              className="w-96"
              value={search}
              onChange={setSearch}
              placeholder="Search video title or creator..."
              icon={SearchMd}
            />
            <Button
              className="font-medium text-xs"
              color="secondary"
              onClick={() => setFiltersOpen((prev) => !prev)}
              iconLeading={<FilterLines className="w-4 h-4" />}
            >
              Filter
            </Button>
          </div>

          {selected.length > 0 && (
            <div className="absolute inset-0 z-20 flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2">
              <div className="flex flex-row items-center gap-2">
                <CloseButton onClick={handleClearSelection} />
                <span className="text-sm font-medium">{selectedCount.toLocaleString()} selected</span>
                <SelectAllPagesButton state={selectAll} />
              </div>
              <div className="flex flex-row gap-2 items-center">
                <ManageCreatorDropdown
                  onMessageCreator={() => setBulkMessageOpen(true)}
                  onAddToList={() => setBulkAddToListOpen(true)}
                  onInviteToCollaboration={() => setBulkInviteOpen(true)}
                  onAddToCohort={() => setBulkAddToCohortOpen(true)}
                  onBlock={() => setBulkBlockOpen(true)}
                  onRemoveFromCollaboration={() => {
                    setBulkPreselectedAction("remove_from_collaboration");
                    setBulkPreselectedCategory("take");
                    setBulkActionModalOpen(true);
                  }}
                  cohortsEnabled
                  blocklistEnabled
                  disabled={selectAll.isFetching}
                />
                <VideoActionsDropdown
                  disabled={selectAll.isFetching}
                  adCampaignDisabledReason={adCampaignDisabledReason}
                  onAddRemoveAdCampaign={() => {
                    setBulkPreselectedAction("remove_from_ad_campaign");
                    setBulkPreselectedCategory("take");
                    setBulkActionModalOpen(true);
                  }}
                  onAddRisk={() => setBulkAddRiskOpen(true)}
                />
              </div>
            </div>
          )}
        </div>

        <FilterPills className="-my-2" />

        <VideoExplorerTable
          rows={tableData}
          timePeriod={timePeriod}
          blocklistEnabled
          onClickRow={(row) => {
            setSelectedVideoId(row.videoId);
            navigate({ search: (prev) => ({ ...prev, video_id: row.videoId }) });
          }}
          selection={{ raw: true, selectedKeys: new Set(selected), onSelectionChange: handleSelectionChange }}
          sorting={{ descriptor, onSortChange: setFromDescriptor }}
          onHiddenSortColumn={sortByTitle}
          adsConnected={adsConnected}
          pagination={{
            page: pagination.page,
            totalPages: Math.ceil((total ?? 0) / pagination.pagination.limit),
            onPageChange: pagination.setPage,
          }}
          hasActiveFilters={hasActiveFilterFields || !!debouncedSearch}
          onClearFilters={resetFilters}
        />
        <VideoDetails
          id={selectedVideoId}
          onClose={() => {
            setSelectedVideoId(undefined);
            navigate({ search: (prev) => ({ ...prev, video_id: undefined }) });
          }}
          isAssurance={isAssurance}
        />

        <Dialog
          open={bulkActionModalOpen}
          onOpenChange={(open) => {
            setBulkActionModalOpen(open);
            if (!open) {
              setBulkPreselectedAction(undefined);
              setBulkPreselectedCategory(undefined);
            }
          }}
        >
          {bulkActionModalOpen && (
            <ActionRecordDialog
              videos={Array.from(selectedVideosData.values())}
              selection={bulkSelection}
              selectedCount={selectedCount}
              onClose={() => {
                clearSelection();
                setBulkActionModalOpen(false);
                setBulkPreselectedAction(undefined);
                setBulkPreselectedCategory(undefined);
              }}
              preselectedAction={bulkPreselectedAction}
              preselectedCategory={bulkPreselectedCategory}
            />
          )}
        </Dialog>

        <AddRiskModal
          selectedVideos={selected}
          open={bulkAddRiskOpen}
          onOpenChange={setBulkAddRiskOpen}
          onSuccess={handleClearSelection}
        />

        {bulkMessageOpen && (
          <MessageCreatorsDialog
            recipients={bulkCreators
              .filter((v) => v.creatorId)
              .map((v) => ({
                creatorId: v.creatorId as string,
                handle: v.creatorName,
                thumbnail: v.thumbnail,
              }))}
            open={bulkMessageOpen}
            onOpenChange={setBulkMessageOpen}
            onSuccess={() => {
              setBulkMessageOpen(false);
              handleClearSelection();
            }}
          />
        )}

        {bulkInviteOpen && (
          <InviteToCollaborationModal
            creators={bulkCreators
              .filter((v) => v.tiktokCreatorOpenId)
              .map((v) => ({
                openId: v.tiktokCreatorOpenId as string,
                username: v.creatorName ?? "",
                nickname: v.creatorName,
                avatar: v.thumbnail,
              }))}
            open={bulkInviteOpen}
            onOpenChange={(open) => {
              setBulkInviteOpen(open);
              if (!open) handleClearSelection();
            }}
          />
        )}

        <AddtoListsModal
          selectedCreators={bulkCreators.map((v) => v.creatorId).filter((id): id is string => !!id)}
          onAddToList={() => {
            setBulkAddToListOpen(false);
            handleClearSelection();
          }}
          open={bulkAddToListOpen}
          onOpenChange={setBulkAddToListOpen}
        />

        <BulkCohortModal
          creatorIds={bulkCreators.map((v) => v.creatorId).filter((id): id is string => !!id)}
          open={bulkAddToCohortOpen}
          onOpenChange={setBulkAddToCohortOpen}
          onApplied={() => {
            setBulkAddToCohortOpen(false);
            handleClearSelection();
          }}
        />

        {bulkBlockOpen && (
          <BlockCreatorModal
            creatorOpenIds={bulkCreators.map((v) => v.tiktokCreatorOpenId).filter((id): id is string => !!id)}
            open={bulkBlockOpen}
            onOpenChange={(open) => {
              setBulkBlockOpen(open);
              if (!open) handleClearSelection();
            }}
          />
        )}
      </div>
      <VideoFilterPanel
        open={filtersOpen}
        close={() => setFiltersOpen(false)}
        clearAll={() => resetFilters()}
        timePeriod={timePeriod}
        withAvailability
      />
    </PageLayout>
  );
};

export const VideoExplorer = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof videoExplorerRoute)["useSearch"]>;

  return (
    <FiltersProvider
      activeFields={[
        "timePeriod",
        "publishDateStart",
        "publishDateEnd",
        "riskTier",
        "riskId",
        "videoRiskStatus",
        "safetyStatus",
        "minGmv",
        "maxGmv",
        "minViews",
        "maxViews",
        "minCtr",
        "maxCtr",
        "minCtor",
        "maxCtor",
        "minOrders",
        "maxOrders",
        "hooks",
        "videoStyles",
        "tactics",
        "sellingPoints",
        "videoLanguages",
        "creatorType",
        "creatorLevel",
        "minFollowers",
        "maxFollowers",
        "maleAudiencePercent",
        "femaleAudiencePercent",
        "age",
        "product",
        "collaborationType",
        "collaboration",
        "campaign",
        "creator",
        "creatorList",
      ]}
      initialValues={urlParams}
    >
      <VideoExplorerContent />
    </FiltersProvider>
  );
};
